<!-- src/views/TransferView.vue -->
<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center px-4 py-20">
    
    <!-- Contenedor principal -->
    <div class="max-w-2xl w-full bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-700 relative overflow-hidden">
      
      <!-- Barra decorativa superior -->
      <div class="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-cyan-400 to-lime-400"></div>

      <!-- Logo y Título -->
      <div class="text-center mb-10">
        <div class="flex items-center justify-center mb-6">
          <div class="relative">
            <div class="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-lime-400 flex items-center justify-center shadow-lg">
              <span class="text-4xl font-bold text-gray-900">TS</span>
            </div>
            <div class="absolute -bottom-1 -right-1 w-6 h-6 bg-lime-400 rounded-full border-4 border-gray-800"></div>
          </div>
          <h1 class="text-4xl md:text-5xl font-bold ml-4 bg-gradient-to-r from-cyan-400 to-lime-400 bg-clip-text text-transparent">
            Transferencias
          </h1>
        </div>
        <p class="text-lg md:text-xl text-gray-300 mt-2">
          Portal para Agencias
        </p>
        <p class="text-sm text-gray-400 mt-2 max-w-md mx-auto">
          Ingresa el código de seguridad y selecciona el rango de fechas (máximo 1 mes).
        </p>
      </div>

      <!-- Mensaje de Error -->
      <div v-if="errorMessage" class="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-400 text-sm text-center animate-pulse">
        {{ errorMessage }}
      </div>

      <!-- Formulario de Consulta -->
      <div class="mb-8 space-y-6">
        
        <!-- Campo: Código Encriptado -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Código de Agencia / Encriptado
          </label>
          <div class="relative group">
            <input
              v-model="formData.codigo"
              type="text"
              placeholder="Ej: YXBwLTE0MTQ3..."
              class="w-full px-6 py-4 bg-gray-700 border-2 border-gray-600 rounded-xl focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 outline-none transition-all text-base font-mono text-white placeholder-gray-500 group-hover:border-gray-500"
            />
            <div class="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg class="w-6 h-6 text-gray-500 group-focus-within:text-cyan-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Campo: Selector de Rango de Fechas -->
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Rango de Fechas (máximo 1 mes)
          </label>
          
          <div class="datepicker-container">
            <date-picker
              v-model="selectedRange"
              type="date"
              range
              format="DD/MM/YYYY"
              :lang="esLang"
              @change="onDateRangeChange"
              placeholder="Seleccionar fecha desde - hasta"
              input-class="custom-date-input"
            />
          </div>
          
          <!-- Validación visual -->
          <p v-if="dateValidationError" class="mt-2 text-sm text-red-400">
            {{ dateValidationError }}
          </p>
          <p v-else-if="selectedRange && selectedRange.length === 2" class="mt-2 text-sm text-lime-400">
            ✓ Rango válido: {{ getDaysDifference() }} días
          </p>
        </div>
        
        <!-- Botón de Consultar -->
        <button
          @click="consultarTransferencia"
          :disabled="!isFormValid || isLoading"
          class="mt-4 w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-lime-500 hover:from-cyan-400 hover:to-lime-400 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold text-lg rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-cyan-500/25 transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
        >
          <svg v-if="isLoading" class="animate-spin h-6 w-6 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          
          <span>{{ isLoading ? 'Consultando...' : 'Consultar Transferencias' }}</span>
        </button>
      </div>

      <!-- Info Footer -->
      <div class="mt-8 pt-6 border-t border-gray-700">
        <div class="flex items-start gap-3 text-sm">
          <svg class="w-5 h-5 text-lime-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="flex-1">
            <p class="font-medium text-gray-200 mb-2">Búsqueda por Rango</p>
            <ul class="space-y-2 text-gray-400">
              <li class="flex items-start gap-2">
                <span class="text-cyan-400 font-bold">•</span>
                <span>Pega el código único de tu agencia.</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-cyan-400 font-bold">•</span>
                <span>Selecciona un rango de fechas (máximo 31 días).</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-cyan-400 font-bold">•</span>
                <span>Confirma para filtrar las transferencias del período.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>

    <!-- Footer Global -->
    <div class="mt-8 text-center">
      <p class="text-gray-400 text-sm">
        TourPass Agency Portal - Desarrollado por
        <a href="https://www.toursolutions.cl" target="_blank" class="text-cyan-400 hover:text-lime-400 font-semibold">TourSolutions</a>
        © {{ new Date().getFullYear() }}
      </p>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/es';
import apiService from '@/services/api';

export default {
  name: 'TransferView',
  components: {
    DatePicker
  },
  data() {
    return {
      formData: {
        codigo: '',
        fechaDesde: '',
        fechaHasta: ''
      },
      selectedRange: [],
      isLoading: false,
      errorMessage: '',
      dateValidationError: '',
      esLang: {
        days: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
        months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        pickers: ['Próximos 7 días', 'Próximos 30 días', 'Semana pasada', 'Mes pasado'],
        placeholder: {
          date: 'Seleccionar fecha',
          dateRange: 'Seleccionar rango'
        }
      }
    };
  },
  computed: {
    isFormValid() {
      return this.formData.codigo.trim() !== '' && 
             this.formData.fechaDesde && 
             this.formData.fechaHasta &&
             !this.dateValidationError;
    }
  },
  mounted() {
    // Establecer rango predeterminado: mes actual
    this.setDefaultRange();
  },
  methods: {
    setDefaultRange() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      
      // Primer día del mes actual
      const startDate = new Date(year, month, 1);
      // Último día del mes actual
      const endDate = new Date(year, month + 1, 0);
      
      // Asignar como objetos Date
      this.selectedRange = [startDate, endDate];
      this.updateFormData();
    },
    
    onDateRangeChange(value) {
      if (!value || value.length !== 2) {
        this.dateValidationError = '';
        this.formData.fechaDesde = '';
        this.formData.fechaHasta = '';
        return;
      }
      
      const daysDiff = this.getDaysDifference();
      
      if (daysDiff > 31) {
        this.dateValidationError = 'El rango no puede superar los 31 días';
        this.formData.fechaDesde = '';
        this.formData.fechaHasta = '';
      } else {
        this.dateValidationError = '';
        this.updateFormData();
      }
    },
    
    updateFormData() {
      if (this.selectedRange && this.selectedRange.length === 2) {
        this.formData.fechaDesde = this.formatDateForAPI(this.selectedRange[0]);
        this.formData.fechaHasta = this.formatDateForAPI(this.selectedRange[1]);
      }
    },
    
    getDaysDifference() {
      if (!this.selectedRange || this.selectedRange.length !== 2) {
        return 0;
      }
      
      const date1 = new Date(this.selectedRange[0]);
      const date2 = new Date(this.selectedRange[1]);
      const oneDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round(Math.abs((date2 - date1) / oneDay));
      return diffDays;
    },
    
    formatDateForAPI(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    async consultarTransferencia() {
      if (!this.isFormValid) return;
      
      this.isLoading = true;
      this.errorMessage = '';

      try {
        // Usar el servicio API centralizado con fechas
        const response = await apiService.getLoginAgencia(
          this.formData.codigo,
          this.formData.fechaDesde,
          this.formData.fechaHasta
        );

        if (response.ok) {
          this.$router.push({
            path: '/dashboard-transferencias',
            query: {
              codigo: this.formData.codigo,
              fecha_desde: this.formData.fechaDesde,
              fecha_hasta: this.formData.fechaHasta
            }
          });
        } else {
          this.errorMessage = response.message || 'Credenciales inválidas';
        }

      } catch (error) {
        console.error('Error en consulta:', error);
        this.errorMessage = error.message || 'Error de conexión.';
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
button { 
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
}
button:active:not(:disabled) { 
  transform: scale(0.98); 
}

/* Contenedor del datepicker */
.datepicker-container {
  position: relative;
}

/* Estilo personalizado del input del datepicker */
::v-deep .custom-date-input {
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: #374151;
  border: 2px solid #4b5563;
  border-radius: 0.75rem;
  color: white;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  cursor: pointer;
}

::v-deep .custom-date-input:hover {
  border-color: #6b7280;
}

::v-deep .custom-date-input:focus {
  border-color: #22d3ee;
  box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.2);
}

::v-deep .custom-date-input::placeholder {
  color: #6b7280;
}

/* Icono de calendario */
::v-deep .mx-icon-calendar {
  color: #9ca3af;
  right: 1rem;
}

/* Tema oscuro personalizado */
::v-deep .mx-datepicker {
  width: 100%;
}

::v-deep .mx-datepicker-popup {
  background-color: #1f2937 !important;
  border-color: #4b5563 !important;
  color: #f3f4f6 !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5) !important;
}

::v-deep .mx-calendar {
  background-color: #1f2937 !important;
  color: #f3f4f6 !important;
}

::v-deep .mx-calendar-header {
  background-color: #374151 !important;
  color: #f3f4f6 !important;
  border-bottom: 1px solid #4b5563;
}

::v-deep .mx-btn {
  color: #22d3ee !important;
}

::v-deep .mx-btn:hover {
  background-color: rgba(34, 211, 238, 0.1) !important;
}

::v-deep .mx-cell-selected {
  background-color: #22d3ee !important;
  color: #111827 !important;
}

::v-deep .mx-cell-in-range {
  background-color: rgba(34, 211, 238, 0.2) !important;
  color: #f3f4f6 !important;
}

::v-deep .mx-calendar-week-mode .mx-cell-selected {
  background-color: #22d3ee !important;
  color: #111827 !important;
}

::v-deep .mx-calendar-header-label {
  color: #f3f4f6 !important;
}

::v-deep .mx-calendar-header-btn {
  color: #22d3ee !important;
}

::v-deep .mx-table th {
  color: #9ca3af !important;
}

::v-deep .mx-cell {
  color: #f3f4f6 !important;
}

::v-deep .mx-cell:hover:not(.mx-cell-selected):not(.mx-cell-in-range) {
  background-color: rgba(34, 211, 238, 0.1) !important;
}

::v-deep .mx-cell-disabled {
  color: #6b7280 !important;
}
</style>